








































































































import { defineComponent, ref, computed, toRefs, onMounted, reactive, PropType, watch, onUnmounted, nextTick } from '@vue/composition-api';
import Container from '@/components/main/Container.vue';
import ResizeObserver from 'resize-observer-polyfill';
import { debounce } from 'lodash';
import useStore from '@/mixins/useStore';

import { Splitpanes, Pane, PaneProps } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';

type PanelNames = 'left' | 'content' | 'right';
type ResizeEventData = { min:number; max: number; size: number }

export default defineComponent({
    name: 'PageLayout',
    components: {
        Container,
        Splitpanes,
        Pane,
    },
    props: {
        // 是否使用Container组件内置样式
        isUsedContainerClass: {
            type: Boolean,
            default: true,
        },
        // 是否使用box,page样式
        isUsedBoxPageClass: {
            type: Boolean,
            default: true,
        },
        // Contain组件下的div样式
        boxPageStyle: {
            type: Object as PropType<any>,
            default: () => ({}),
        },
        // header样式属性
        headerStyle: {
            type: Object as PropType<any>,
            default: () => ({ marginBottom: '10px' }),
        },
        // content样式属性
        contentStyle: {
            type: Object as PropType<any>,
            default: () => ({}),
        },
        // left样式属性
        leftStyle: {
            type: Object as PropType<any>,
            default: () => ({ marginRight: '10px' }),
        },
        // right样式属性
        rightStyle: {
            type: Object as PropType<any>,
            default: () => ({ marginLeft: '10px' }),
        },
        // footer样式属性
        footerStyle: {
            type: Object as PropType<any>,
            default: () => ({ marginTop: '10px' }),
        },
        // 是否有Tabs
        hasTabsRef: {
            type: Boolean,
            default: false,
        },
        resizePane: {
            type: Boolean,
            default: false,
        },
        // left面板的size
        leftPaneSize: {
            type: Object as PropType<PaneProps>,
            default: () => ({ size: 10, maxSize: 30, minSize: 5 }),
        },
        // right样式属性
        rightPaneSize: {
            type: Object as PropType<PaneProps>,
            default: () => ({ size: 10, maxSize: 30, minSize: 5 }),
        },
        // contentWrapperStyle样式属性
        contentWrapperStyle: {
            type: Object as PropType<any>,
            default: () => ({}),
        },
    },
    setup(props, ctx) {
        const headerRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const centerRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const leftRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const contentTopRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const contentRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const contentBottomRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const rightRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const footerRef = ref<InstanceType <typeof HTMLDivElement> | null>(null);
        const centerProperty = reactive({ width: 0, height: 0 });
        const contentProperty = reactive({ height: 0 });
        let resizeObserver : ResizeObserver | null = null;
        const isMounted = ref(false);

        // const panels = ref(['container']);
        const panelSizeInfo = reactive<Record<PanelNames, ResizeEventData>>({
            left: { min: 0, max: 0, size: 0 },
            content: { min: 0, max: 0, size: 0 },
            right: { min: 0, max: 0, size: 0 },
        });

        const { userModule, companyModule } = useStore();

        const { auth_technology_status } = companyModule.useState(['auth_technology_status']);

        const { roleId, isOwner } = userModule.useState(['roleId', 'isOwner']);

        const remoteTechnologyStatus = computed(() => {
            return (
                auth_technology_status.value === '1' &&
                ['0', '1'].indexOf(roleId.value) !== -1
            );
        });

        const containerStyle = computed(() => {
            if (props.isUsedContainerClass) {
                if (remoteTechnologyStatus.value && isOwner.value === '0') {
                    return {
                        height: 'calc(100vh - 100px)',
                    };
                }
                if (remoteTechnologyStatus.value || isOwner.value === '0') {
                    return {
                        height: 'calc(100vh - 80px)',
                    };
                }
                return {};
            }
            return {};
        });

        // 计算centerProperty的高度
        const calcCenter = () => {
            centerProperty.width = centerRef.value?.clientWidth || 0;
            let height = props.hasTabsRef ? 138 : 100; // Tabs默认高度：'45px'
            let wrapContentHeight = contentProperty.height; // content的总高度
            if (remoteTechnologyStatus.value) {
                height += 17;
            }
            if (isOwner.value === '0') {
                height += 17;
            }
            if (headerRef.value) {
                height += headerRef.value.offsetHeight + parseInt(props.headerStyle.marginBottom, 10);
            }
            if (footerRef.value) {
                height += footerRef.value.offsetHeight + parseInt(props.footerStyle.marginTop, 10);
            }
            if (contentTopRef.value) {
                wrapContentHeight += contentTopRef.value.offsetHeight;
            }
            if (contentBottomRef.value) {
                wrapContentHeight += contentBottomRef.value.offsetHeight;
            }
            if (wrapContentHeight > window.innerHeight - height) {
                centerProperty.height = wrapContentHeight;
            } else {
                centerProperty.height = window.innerHeight - height < 300 ? 300 : window.innerHeight - height;
            }
        };

        // 计算content的高度
        const calcContent = () => {
            let height = props.hasTabsRef ? 138 : 100; // Tabs默认高度：'45px'
            if (remoteTechnologyStatus.value) {
                height += 17;
            }
            if (isOwner.value === '0') {
                height += 17;
            }
            if (headerRef.value) {
                height += headerRef.value.offsetHeight + parseInt(props.headerStyle.marginBottom, 10);
            }
            if (footerRef.value) {
                height += footerRef.value.offsetHeight + parseInt(props.footerStyle.marginTop, 10);
            }
            if (contentTopRef.value) {
                height += contentTopRef.value.offsetHeight;
            }
            if (contentBottomRef.value) {
                height += contentBottomRef.value.offsetHeight;
            }
            contentProperty.height = window.innerHeight - height < 300 ? 300 : window.innerHeight - height;
        };

        // 整合计算
        const calcHeight = () => {
            calcContent();
            calcCenter();
        };

        const debounceCalcHeight = debounce(calcHeight, 200);

        // 监听 resize 变化
        const observeResize = () => {
            resizeObserver = new ResizeObserver(() => {
                debounceCalcHeight();
            });
            if (headerRef.value) {
                resizeObserver.observe(headerRef.value);
            }
            if (leftRef.value) {
                resizeObserver.observe(leftRef.value);
            }
            if (contentRef.value) {
                resizeObserver.observe(contentRef.value);
            }
            if (rightRef.value) {
                resizeObserver.observe(rightRef.value);
            }
            if (footerRef.value) {
                resizeObserver.observe(footerRef.value);
            }
        };

        /**
         * 计算可以变化宽度面板的百分比
         * Splitpanes 这个库控制大小的时候只支持百分比，哪怕传入 200px 也会转换为 200
         * 判断如果传入的 size 是数字类型的就直接返回，如果带px单位的就转换为百分比
         */
        const calcPaneSize = (paneSize: PaneProps, sizeInfo: ResizeEventData) => {
            if (!props.resizePane) {
                return {};
            }

            if (!isMounted.value && paneSize.minSize) {
                return {
                    style: { width: paneSize.minSize },
                };
            }
            const result: Partial<PaneProps> = {};
            Object.keys(paneSize).forEach((key: string) => {
                const value = paneSize[key as keyof PaneProps];
                if (!value) {
                    return;
                }
                if (typeof value === 'number' || /\d/.test(value)) {
                    result[key as keyof PaneProps] = value;
                }
                if (typeof value === 'string' && !Number.isNaN(parseInt(value, 10))) {
                    const unit = value.replace(/\d/g, '');
                    // 纯数字单位
                    let _value = parseInt(value, 10);
                    if (unit === 'px' && centerProperty.width) {
                        _value = (_value / centerProperty.width) * 100;
                    }
                    result[key as keyof PaneProps] = _value;
                }
            });
            if (sizeInfo.size !== 0) {
                result.size = Math.max(sizeInfo.size, result.minSize as number || 0);
            }
            return result;
        };

        // 计算左右面板的宽度的百分比
        const leftPaneProps = computed(() => {
            return calcPaneSize(props.leftPaneSize, panelSizeInfo.left);
        });
        const rightPaneProps = computed(() => {
            // return calcPaneSize(props.rightProperty);
            return calcPaneSize(props.rightPaneSize, panelSizeInfo.right);
        });

        const panelNames = computed(() => {
            const panels:PanelNames[] = [];
            if (leftRef.value) {
                panels.push('left');
            }
            if (contentRef.value) {
                panels.push('content');
            }
            if (rightRef.value) {
                panels.push('right');
            }
            return panels;
        });

        // 记录面板宽度的变化
        const handleOnPanelResized = debounce((event: ResizeEventData[]) => {
            calcHeight();
            panelNames.value.forEach((name, index) => {
                panelSizeInfo[name] = event[index] || { min: 0, max: 0, size: 0 };
            });
        }, 100, { maxWait: 300 });

        const canResizePane = computed(() => {
            return isMounted.value && props.resizePane;
        });

        onMounted(() => {
            calcHeight();
            observeResize();
            nextTick(() => {
                isMounted.value = true;
            });
        });

        onUnmounted(() => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        });

        return {
            headerRef,
            centerRef,
            leftRef,
            contentTopRef,
            contentRef,
            contentBottomRef,
            rightRef,
            footerRef,
            centerProperty,
            contentProperty,
            containerStyle,

            leftPaneProps,
            rightPaneProps,
            handleOnPanelResized,
            panelSizeInfo,
            canResizePane,
        };
    },
});
